// src/posts/Fsdp.jsx
// Scaling Training · Part 5 of 5 — PyTorch FSDP: ZeRO Inside PyTorch.
// TAGS FOR REGISTRATION: ['training', 'distributed', 'pytorch']
// EXCERPT: ZeRO was a DeepSpeed extension; FSDP bakes the same memory-sharding idea into PyTorch core. The FlatParameter, the per-unit all-gather/free dance, and the prefetching that hides the communication behind compute.
import React, { useEffect, useRef, useState } from 'react';
import usePageMeta from '../usePageMeta';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './PostChrome.css';
import './Fsdp.css';

/* ============================================================
   KaTeX wrapper
   ============================================================ */
function Katex({ tex, block = false, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || !ref.current) return;
      if (window.katex) {
        try {
          window.katex.render(tex, ref.current, { displayMode: block, throwOnError: false });
        } catch {
          if (ref.current) ref.current.textContent = tex;
        }
      } else {
        setTimeout(render, 60);
      }
    };
    render();
    return () => { cancelled = true; };
  }, [tex, block]);
  return block
    ? <div ref={ref} className={`fs-katex-block ${className}`} />
    : <span ref={ref} className={`fs-katex-inline ${className}`} />;
}

/* ============================================================
   Widget — Forward-pass Gantt chart, naive vs prefetched.
   Two rows (COMPUTE, COMM), N time slots, 3 units.
   ============================================================ */
const UNIT_COLORS = [
  'hsl(218, 70%, 50%)',
  'hsl(35, 85%, 55%)',
  'hsl(280, 50%, 55%)',
];

// Each timeline entry: { slot: index, type: 'compute' | 'ag', unit: 0/1/2 }
// Naive: each (AG, Compute) pair runs serially.
const NAIVE = [
  // t=0
  { row: 'comm',    unit: 0, label: 'AG U1' },
  // t=1
  { slot: 1, row: 'compute', unit: 0, label: 'fwd U1' },
  // t=2
  { slot: 2, row: 'comm',    unit: 1, label: 'AG U2' },
  // t=3
  { slot: 3, row: 'compute', unit: 1, label: 'fwd U2' },
  // t=4
  { slot: 4, row: 'comm',    unit: 2, label: 'AG U3' },
  // t=5
  { slot: 5, row: 'compute', unit: 2, label: 'fwd U3' },
];
const NAIVE_SLOTS = 6;

// Prefetched: while U1 compute runs, U2 all-gather is already on the wire.
const PREFETCH = [
  // t=0
  { slot: 0, row: 'comm',    unit: 0, label: 'AG U1' },
  // t=1
  { slot: 1, row: 'comm',    unit: 1, label: 'AG U2 (prefetch)' },
  { slot: 1, row: 'compute', unit: 0, label: 'fwd U1' },
  // t=2
  { slot: 2, row: 'comm',    unit: 2, label: 'AG U3 (prefetch)' },
  { slot: 2, row: 'compute', unit: 1, label: 'fwd U2' },
  // t=3
  { slot: 3, row: 'compute', unit: 2, label: 'fwd U3' },
];
const PREFETCH_SLOTS = 4;

function fillSlot0Naive() {
  return { slot: 0, row: 'comm', unit: 0, label: 'AG U1' };
}
const NAIVE_FIXED = [fillSlot0Naive(), ...NAIVE.slice(1)];

function PrefetchGantt() {
  const [mode, setMode] = useState('prefetch');
  const data = mode === 'naive' ? NAIVE_FIXED : PREFETCH;
  const slots = mode === 'naive' ? NAIVE_SLOTS : PREFETCH_SLOTS;
  const speedup = (NAIVE_SLOTS / PREFETCH_SLOTS).toFixed(2);

  const W = 760, H = 230;
  const padL = 92, padR = 24, padT = 32, padB = 50;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const slotW = innerW / 6; // fix width to the naive (slower) timeline so the bars are comparable
  const rowH = innerH / 2 - 8;
  const rowGap = 16;
  const computeY = padT;
  const commY    = padT + rowH + rowGap;

  // For the prefetch mode, we want bars to still align to the same slot width,
  // so naive takes 6 slots and prefetch takes 4 slots (so it's visibly shorter).

  const ROW_LABELS = [
    { y: computeY + rowH / 2, label: 'COMPUTE', sub: '(GPU SMs)' },
    { y: commY + rowH / 2,    label: 'COMM',    sub: '(NCCL)' },
  ];

  return (
    <div className="viz-panel fs-widget">
      <div className="fs-controls">
        <div className="fs-tabs">
          <button onClick={() => setMode('naive')}
                  className={`fs-tab ${mode === 'naive' ? 'is-active' : ''}`}>
            Naive (no overlap)
          </button>
          <button onClick={() => setMode('prefetch')}
                  className={`fs-tab ${mode === 'prefetch' ? 'is-active' : ''}`}>
            FSDP (prefetch + overlap)
          </button>
        </div>
        <div className="fs-speedup">
          {mode === 'prefetch'
            ? <><strong>{speedup}×</strong> faster wall-clock</>
            : <span className="fs-mut">baseline</span>}
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="fs-svg" preserveAspectRatio="xMidYMid meet">
        {/* row labels */}
        {ROW_LABELS.map((r) => (
          <g key={r.label}>
            <text x={padL - 10} y={r.y} textAnchor="end" fontSize="12"
                  fontFamily="var(--post-mono)" fontWeight="600"
                  fill="var(--post-text)" alignmentBaseline="middle">{r.label}</text>
            <text x={padL - 10} y={r.y + 14} textAnchor="end" fontSize="10"
                  fontFamily="var(--post-mono)" fill="var(--post-text-mut)"
                  alignmentBaseline="middle">{r.sub}</text>
          </g>
        ))}

        {/* row backgrounds */}
        <rect x={padL} y={computeY} width={innerW} height={rowH}
              fill="#f8fafc" stroke="var(--post-border)" rx={4} />
        <rect x={padL} y={commY} width={innerW} height={rowH}
              fill="#f8fafc" stroke="var(--post-border)" rx={4} />

        {/* slot tick lines */}
        {Array.from({ length: 7 }).map((_, i) => (
          <line key={i} x1={padL + i * slotW} y1={padT - 4} x2={padL + i * slotW} y2={padT + innerH + 4}
                stroke="var(--post-border)" strokeOpacity="0.4" strokeDasharray="2 4" />
        ))}

        {/* tick labels */}
        {Array.from({ length: slots + 1 }).map((_, i) => (
          <text key={i} x={padL + i * slotW} y={padT + innerH + 18}
                textAnchor="middle" fontSize="10"
                fontFamily="var(--post-mono)"
                fill={i === slots ? 'var(--post-accent)' : 'var(--post-text-mut)'}
                fontWeight={i === slots ? 600 : 400}>
            t{i}{i === slots ? ' · done' : ''}
          </text>
        ))}

        {/* bars */}
        {data.map((b, idx) => {
          const x = padL + b.slot * slotW + 3;
          const y = b.row === 'compute' ? computeY + 3 : commY + 3;
          const w = slotW - 6;
          const h = rowH - 6;
          const color = UNIT_COLORS[b.unit];
          const isAG = b.row === 'comm';
          return (
            <g key={idx}>
              <rect x={x} y={y} width={w} height={h} rx={5}
                    fill={isAG ? '#fff' : color}
                    stroke={color} strokeWidth="1.8"
                    strokeDasharray={isAG ? '4 3' : undefined} />
              <text x={x + w / 2} y={y + h / 2 + 4}
                    textAnchor="middle" fontSize="11"
                    fontFamily="var(--post-mono)" fontWeight="600"
                    fill={isAG ? color : '#fff'}>
                {b.label}
              </text>
            </g>
          );
        })}
      </svg>

      <p className="fs-caption">
        Three FSDP units (one colour each) marching through a forward pass.
        <strong> Naive</strong>: each unit's parameters must be all-gathered{' '}
        before its compute can start; both tracks stall waiting on each
        other. <strong>FSDP</strong>: while unit 1's compute runs, unit 2's
        all-gather is already on the NCCL stream; the comm row stays busy
        while the compute row stays busy. Wall-clock drops accordingly.
      </p>
    </div>
  );
}

/* ============================================================
   Code block — FSDP wrap a transformer
   ============================================================ */
const CODE = `# Wrap a transformer with FSDP. ShardingStrategy chooses ZeRO stage:
#   FULL_SHARD     ≈ ZeRO-3 (params + grads + optimizer state, all sharded)
#   SHARD_GRAD_OP  ≈ ZeRO-2 (grads + optimizer state)
#   NO_SHARD       = plain DDP
#   HYBRID_SHARD   = FULL_SHARD inside a node, replication across nodes
import torch
from torch.distributed.fsdp import (
    FullyShardedDataParallel as FSDP,
    ShardingStrategy,
    MixedPrecision,
)
from torch.distributed.fsdp.wrap import transformer_auto_wrap_policy

# Choose which sub-modules become FSDP "units" — usually one per block.
auto_wrap_policy = lambda module, recurse, **kw: isinstance(module, TransformerBlock)

mp = MixedPrecision(
    param_dtype   = torch.bfloat16,  # forward/backward in bf16
    reduce_dtype  = torch.bfloat16,  # gradient reduce-scatter in bf16
    buffer_dtype  = torch.bfloat16,
)

model = FSDP(
    model,
    sharding_strategy = ShardingStrategy.FULL_SHARD,
    auto_wrap_policy  = auto_wrap_policy,
    mixed_precision   = mp,
    device_id         = torch.cuda.current_device(),
)

# That's it — the rest of the training loop is unchanged.
# Each FSDP unit's parameters get all-gathered before its forward,
# freed afterwards, all-gathered again before its backward, and
# reduce-scattered when backward completes.
`;

function CodeBlock() {
  return (
    <details className="post-code-details fs-code">
      <summary>
        <span className="post-code-summary-icon">›</span>
        <span>PyTorch — wrapping a transformer with FSDP</span>
        <span className="post-code-summary-hint">click to expand</span>
      </summary>
      <SyntaxHighlighter
        language="python"
        style={oneLight}
        customStyle={{ background: 'transparent', margin: 0, padding: 0, fontSize: '13.5px' }}
        codeTagProps={{ style: { background: 'transparent', textShadow: 'none' } }}
      >
        {CODE}
      </SyntaxHighlighter>
    </details>
  );
}

/* ============================================================
   Page
   ============================================================ */
export default function Fsdp() {
  usePageMeta({
    title: 'PyTorch FSDP: ZeRO Inside PyTorch',
    description: 'ZeRO was a DeepSpeed extension; FSDP bakes the same memory-sharding idea into PyTorch core. The FlatParameter abstraction, the per-unit all-gather/free dance, and the prefetching that hides communication behind compute.',
    slug: 'fsdp',
    publishedDate: '2026-07-02',
    keywords: ['FSDP', 'PyTorch', 'ZeRO', 'distributed', 'training', 'data parallel'],
  });

  return (
    <article className="post-2026 fs-post">
      <div className="post-wrap">
        <div className="post-hero">
          <div className="post-series-tag">
            <span className="post-live-dot" aria-hidden="true" />Scaling Training · Part 5 of 5
          </div>
          <h1>PyTorch FSDP: ZeRO Inside PyTorch</h1>
          <p className="post-lede">
            <a className="post-link" href="/blog/zero">ZeRO</a> arrived as a
            DeepSpeed extension that grafted onto PyTorch from the outside —
            custom optimizer wrappers, hooks fighting autograd, the works.
            FSDP is what you get when the same idea is designed{' '}
            <em>into</em> PyTorch core instead. Same memory wins, fewer
            sharp edges, one new abstraction worth understanding.
          </p>
          <div className="post-byline">
            By <strong>Bernhard Walser</strong> &amp;{' '}
            <a className="post-link" href="https://www.anthropic.com/claude" target="_blank" rel="noreferrer"><strong>Claude</strong></a>
            {' '}(Anthropic) · co-written and co-designed ·{' '}
            <a className="post-link" href="https://www.linkedin.com/in/bernhardwalser/" target="_blank" rel="noreferrer">LinkedIn</a>
            {' · '}
            <a className="post-link" href="https://github.com/berniwal" target="_blank" rel="noreferrer">GitHub</a>
          </div>
        </div>

        <h2 className="reveal">Why this needed a different implementation</h2>
        <p>
          DeepSpeed's ZeRO works, but it sits on top of PyTorch as an
          external library: it wraps the optimizer, registers backward
          hooks, redirects parameter access through a shim. Most of the
          time you don't notice; sometimes you fight it. Mixed precision,
          gradient clipping, parameter loading and checkpoint formats all
          had their own integration story.
        </p>
        <p>
          FSDP rebuilt the same algorithm inside PyTorch with full access
          to the Tensor, Autograd, and CUDA-allocator subsystems. Same{' '}
          <Katex tex="\sim\!16\Psi / N" /> peak memory under{' '}
          <code>FULL_SHARD</code>, same <Katex tex="\sim\!3\Psi" /> comm
          per step, same <Katex tex="\sim\!1.5\times" /> overhead vs DDP.
          What changed is everything around it — and one new abstraction
          worth knowing about.
        </p>

        <h2 className="reveal">The FlatParameter</h2>
        <p>
          NCCL collectives like all-gather and reduce-scatter pay a fixed
          per-call latency overhead. They're efficient when you call them
          with one large, contiguous tensor — and miserable when called
          repeatedly with small ones. A transformer block has dozens of
          small parameter tensors (Q, K, V projections, MLP weights and
          biases, LayerNorm scale/shift, …). If you all-gather them
          individually you'd spend the whole step in NCCL setup.
        </p>
        <p>
          FSDP's <strong>FlatParameter</strong> sidesteps that. For each
          FSDP unit (typically one transformer block), all parameters get
          concatenated into a single 1D tensor:
        </p>
        <Katex
          block
          tex={'\\text{FlatParam} \\;=\\; [\\,W_Q^{\\text{flat}} \\,\\Vert\\, W_K^{\\text{flat}} \\,\\Vert\\, W_V^{\\text{flat}} \\,\\Vert\\, W_O^{\\text{flat}} \\,\\Vert\\, W_{\\text{MLP1}}^{\\text{flat}} \\,\\Vert\\, \\ldots\\,]'}
        />
        <p>
          That single tensor is what gets sharded across the{' '}
          <Katex tex="N" /> workers. The original parameter tensors stay
          accessible — they're views into the (unsharded) FlatParameter
          when it's materialized, so gradients accumulate into the right
          slots automatically. One large collective per unit instead of
          dozens of tiny ones.
        </p>

        <h2 className="reveal">The lifecycle of one step</h2>
        <p>
          Under <code>FULL_SHARD</code>, each FSDP unit goes through the
          same dance on every training step:
        </p>
        <ul className="fs-stack">
          <li>
            <strong>Pre-forward.</strong>{' '}
            <strong>all-gather</strong> the unit's FlatParameter — every
            GPU contributes its shard, ends up holding the full unsharded
            tensor. Views into it become the live parameters.
          </li>
          <li>
            <strong>Forward.</strong> Run the unit's layers on the
            unsharded params.
          </li>
          <li>
            <strong>Post-forward.</strong> <strong>Free</strong> all
            shards except the GPU's local one. Memory budget drops back
            to <Katex tex="\Psi/N" /> for this unit.
          </li>
          <li>
            <strong>Pre-backward.</strong> all-gather again — the
            unsharded params are needed for the backward computation too.
          </li>
          <li>
            <strong>Backward.</strong> Compute gradients (in the
            unsharded layout).
          </li>
          <li>
            <strong>Post-backward.</strong>{' '}
            <strong>reduce-scatter</strong> the gradients so each GPU
            ends up with just its shard's gradient. Free the unsharded
            params. The optimizer step touches only the GPU's local
            shards.
          </li>
        </ul>
        <p>
          Two all-gathers per unit per step, plus one reduce-scatter. For
          a 96-layer transformer with one unit per block: 192 + 96 = 288
          collectives per step. That sounds catastrophic — and would be,
          without the next trick.
        </p>

        <h2 className="reveal">Prefetching hides the comm</h2>
        <p>
          The naive serialisation would have every unit wait for its
          all-gather to finish before starting compute. FSDP runs the
          all-gather on a <em>separate CUDA stream</em> so it can overlap
          with compute on the default stream. While unit 1 is computing,
          unit 2's all-gather is already on the NCCL wire; by the time
          compute reaches unit 2 its params are ready.
        </p>
        <p>
          Toggle the two modes below to see what the overlap buys you:
        </p>
        <PrefetchGantt />
        <p>
          The same trick runs in reverse during backward. FSDP records
          the forward execution order during the first iteration and uses
          it as a proxy to predict the backward order, so it can prefetch
          unit <em>k − 1</em>'s all-gather while unit <em>k</em>'s
          reduce-scatter is still on the wire. The paper measures ~18%
          end-to-end speedup on GPT-175B from prefetching alone.
        </p>
        <p>
          There's a catch: FSDP's allocator can over-allocate if the CPU
          races ahead of the GPU and queues too many unsharded
          FlatParameters at once. The <strong>rate limiter</strong>
          {' '}caps in-flight all-gathers to 2 (the minimum needed for
          overlap), blocking the CPU thread otherwise. On models where
          the allocator was thrashing, the paper reports ~5× speedup just
          from rate-limiting.
        </p>

        <h2 className="reveal">Sharding strategies</h2>
        <p>
          FSDP exposes the ZeRO ladder as a single enum:
        </p>
        <ul className="fs-stack">
          <li>
            <strong>FULL_SHARD</strong> — params, gradients, optimizer
            state all sharded. <Katex tex="\equiv" /> ZeRO-3. Maximum
            memory savings, 1.5× DDP comm.
          </li>
          <li>
            <strong>SHARD_GRAD_OP</strong> — gradients + optimizer state
            sharded, params replicated. <Katex tex="\equiv" /> ZeRO-2.
            Comm matches DDP. Use when params fit but optimizer state
            doesn't.
          </li>
          <li>
            <strong>NO_SHARD</strong> — vanilla DDP. Use when the model
            comfortably fits on every GPU and you just want data
            parallelism.
          </li>
          <li>
            <strong>HYBRID_SHARD</strong> — FULL_SHARD inside a node,
            then plain replication across nodes. Saturates fast intra-node
            NVLink with the sharding traffic, keeps inter-node IB doing
            cheap all-reduce. The sweet spot for many real clusters where
            the model fits per-node under FULL_SHARD but you want data
            parallelism across nodes for throughput.
          </li>
        </ul>

        <h2 className="reveal">In practice</h2>
        <p>
          FSDP is the recommended way to scale large-model training in
          modern PyTorch. Wrapping a model is roughly one screen of code:
        </p>
        <CodeBlock />
        <p>
          The <code>auto_wrap_policy</code> decides which sub-modules
          become FSDP units. Default policies wrap one transformer block
          per unit, which is the sweet spot — smaller units mean more
          collectives, larger units mean higher peak memory. The mixed
          precision config lets you keep parameters in fp32 (sharded) but
          run compute in bf16, with bf16 reduce-scatter.
        </p>

        <h2 className="reveal">What changed since: FSDP2</h2>
        <p>
          The FSDP described above is what PyTorch now retroactively calls{' '}
          <strong>FSDP1</strong>. A revamped{' '}
          <code>torch.distributed.fsdp.fully_shard</code> API —{' '}
          <strong>FSDP2</strong> — was iterated on through PyTorch's 2.x
          releases and is now the recommended API for new code. The
          algorithm is the same; the plumbing is different in three ways
          worth knowing about.
        </p>
        <ul className="fs-stack">
          <li>
            <strong>Per-parameter sharding via DTensor.</strong> Instead
            of flattening a unit's many parameters into one
            FlatParameter, every parameter becomes a sharded{' '}
            <code>DTensor</code> on its own. The clever 2023 abstraction
            was load-bearing for NCCL efficiency, but PyTorch's NCCL
            integration got fast enough at small tensors that the
            FlatParameter's costs (padding, friction with frozen params,
            opaque parameter views) stopped being worth it.
          </li>
          <li>
            <strong>Composes cleanly with TP and PP.</strong> Because
            everything is a <code>DTensor</code>, the same primitive
            stack works for FSDP, tensor parallelism (the{' '}
            <a className="post-link" href="/blog/megatron">Megatron</a>{' '}
            post you just read), and pipeline parallel. The 2023 FSDP
            had its own ad-hoc sharding plumbing that fought with TP;
            FSDP2 was designed to combine.
          </li>
          <li>
            <strong>Frozen parameters and tied embeddings work
            properly.</strong> The shared-parameter and LoRA pain
            points from the caveats below largely go away. Frozen
            parameters can be excluded from all-gather/reduce-scatter
            entirely, which is exactly what you want for LoRA-style
            fine-tuning — the base model's billions of frozen params
            never touch the wire.
          </li>
        </ul>
        <p>
          The 2023 paper is still the right place to start for the{' '}
          <em>algorithm</em> — the FlatParameter explanation is what
          makes the all-gather/free dance clickable. But if you're
          writing training code today, reach for{' '}
          <code>fully_shard</code> rather than{' '}
          <code>FullyShardedDataParallel</code>.
        </p>

        <h2 className="reveal">Caveats and edge cases</h2>
        <p className="fs-mut" style={{ fontStyle: 'italic', fontSize: '0.92rem' }}>
          The caveats below apply to the original (FSDP1) implementation.
          FSDP2 fixes most of them — see the section just above. Listed
          here because plenty of production code still runs on the older
          API.
        </p>
        <p>
          <strong>Shared parameters.</strong> If two FSDP units would
          both contain the same parameter (tied input/output embeddings,
          for instance), the FlatParameter abstraction breaks — the
          parameter has to live in exactly one unit. The usual fix is to
          hoist the shared parameter into a parent unit that encloses
          both users.
        </p>
        <p>
          <strong>Frozen parameters (LoRA).</strong> Frozen parameters
          inside an FSDP unit still get all-gathered every step, which
          wastes bandwidth. For LoRA-style fine-tuning where the base
          model is frozen and only adapters train, you want the frozen
          base params in a separate (and ideally non-FSDP) module so the
          all-gather doesn't fire repeatedly. Modern PEFT integrations
          handle this automatically.
        </p>
        <p>
          <strong>Mathematical equivalence breaks for some optimizers.</strong>{' '}
          Anything that needs <em>global</em> per-parameter information —
          gradient clipping by global norm, Adafactor's second-moment
          aggregation, LAMB — has to do an extra reduction to compute
          those statistics across shards. FSDP provides a sharded
          gradient clipper; for less common optimizers you may need to
          do this yourself.
        </p>

        <h2 className="reveal">References</h2>
        <div className="post-refs">
          <div className="head ref-cite">Cite</div>
          <div className="head ref-title">Title</div>
          <div className="head ref-link">Link</div>

          <div className="ref-cite">Zhao et al. 2023</div>
          <div>
            <div className="ref-title">
              <a href="https://arxiv.org/abs/2304.11277" target="_blank" rel="noreferrer">PyTorch FSDP: Experiences on Scaling Fully Sharded Data Parallel</a>
            </div>
            <div className="ref-note">The FSDP paper. FlatParameter, prefetching, rate limiter, HYBRID_SHARD, scaling to GPT-175B.</div>
          </div>
          <div className="ref-link"><a href="https://arxiv.org/abs/2304.11277" target="_blank" rel="noreferrer">arxiv.org/abs/2304.11277</a></div>

          <div className="ref-cite">PyTorch docs</div>
          <div>
            <div className="ref-title">
              <a href="https://pytorch.org/docs/stable/fsdp.html" target="_blank" rel="noreferrer">torch.distributed.fsdp (FSDP1)</a>
            </div>
            <div className="ref-note">The original API. <code>ShardingStrategy</code>, <code>auto_wrap_policy</code>, <code>MixedPrecision</code> knobs.</div>
          </div>
          <div className="ref-link"><a href="https://pytorch.org/docs/stable/fsdp.html" target="_blank" rel="noreferrer">pytorch.org</a></div>

          <div className="ref-cite"></div>
          <div>
            <div className="ref-title">
              <a href="https://pytorch.org/docs/stable/distributed.fsdp.fully_shard.html" target="_blank" rel="noreferrer">torch.distributed.fsdp.fully_shard (FSDP2)</a>
            </div>
            <div className="ref-note">The modern per-parameter API. Recommended for new code; composes with TP and PP via DTensor.</div>
          </div>
          <div className="ref-link"><a href="https://pytorch.org/docs/stable/distributed.fsdp.fully_shard.html" target="_blank" rel="noreferrer">pytorch.org</a></div>

          <div className="ref-cite">Earlier posts</div>
          <div>
            <div className="ref-title">
              <a href="/blog/zero">ZeRO: Sharding the Optimizer</a>
            </div>
            <div className="ref-note">The algorithm FSDP implements. Read this first if the FULL_SHARD memory math feels new.</div>
          </div>
          <div className="ref-link"><a href="/blog/zero">/zero</a></div>

          <div className="ref-cite"></div>
          <div>
            <div className="ref-title">
              <a href="/blog/ring-all-reduce">Ring All-Reduce</a>
            </div>
            <div className="ref-note">Where the all-gather and reduce-scatter primitives come from.</div>
          </div>
          <div className="ref-link"><a href="/blog/ring-all-reduce">/ring-all-reduce</a></div>
        </div>

        <footer className="post-footer">
          <p>
            <strong>Part 5 of Scaling Training</strong> · Previous:{' '}
            <a className="post-link" href="/blog/megatron">Megatron-LM: Tensor Parallelism</a>
            {' · '}Start of the series:{' '}
            <a className="post-link" href="/blog/gradient-checkpointing">Gradient Checkpointing</a>.
          </p>
          <p style={{ marginBottom: 0 }}>
            Bernhard Walser · ML Engineer, Digitec Galaxus · ETH Computer Science ·{' '}
            <a className="post-link" href="https://www.linkedin.com/in/bernhardwalser/" target="_blank" rel="noreferrer">LinkedIn</a>
            {' · '}
            <a className="post-link" href="https://github.com/berniwal" target="_blank" rel="noreferrer">GitHub</a>
          </p>
        </footer>
      </div>
    </article>
  );
}
